import { z } from 'zod';

function getBase64FileSize(base64String: string): number {
  let equalityCount = 0;
  if (base64String.at(-2) === '=') {
    equalityCount = 2;
  } else if (base64String.at(-1) === '=') {
    equalityCount = 1;
  }
  return base64String.length * (3 / 4) - equalityCount;
}

const fileSizeUnits = ['b', 'kb', 'mb'] as const;
type FileSizeUnit = (typeof fileSizeUnits)[number];
type FileSize = `${number}${FileSizeUnit}`;

const bytes = 1;
const kilobytes = bytes * 1024;
const megabytes = kilobytes * 1024;

const valueMapper: Record<FileSizeUnit, number> = {
  b: bytes,
  kb: kilobytes,
  mb: megabytes,
};

function getNumericValue(fileSize: FileSize) {
  const fileSizeUnit = fileSizeUnits
    .slice()
    .reverse()
    .find((unit) => fileSize.endsWith(unit));
  if (!fileSizeUnit) {
    throw new Error(`${fileSize} is not a valid file size value.`);
  }

  const size = fileSize.replace(fileSizeUnit, '');
  const numericSize = Number(size);
  if (Number.isNaN(numericSize)) {
    throw new Error(`${size} is not a valid number for file size value.`);
  }

  return numericSize * valueMapper[fileSizeUnit];
}

export function createZodMaxBase64SizeValidator(
  size: FileSize,
  message = `File should be less than ${size}`,
) {
  const numericSize = getNumericValue(size);
  return z.string().refine((val) => getBase64FileSize(val) <= numericSize, message);
}

export function createZodMaxBase64MultiSizeValidator(
  size: FileSize,
  message = `Files should be less than ${size} in total`,
) {
  const numericSize = getNumericValue(size);

  return z.array(z.string()).refine((val) => {
    const strVal = val.join('');
    return getBase64FileSize(strVal) <= numericSize;
  }, message);
}
